/**
 * Global registry of live panel state.
 *
 * Each panel component registers its current on-screen state (ticker, filters,
 * columns, indicators, the tickers it lists, etc.) so the AI Companion can read
 * it — via the `read_panel_state` tool AND when the user `@`-mentions the panel
 * in chat. Without this, the AI only knows a panel's type/id, not what's IN it.
 *
 * This is NOT a React store — it's a plain Map that panels write to and tools
 * read from. No subscriptions, no re-renders. A snapshot of what's on screen now.
 */

import { useEffect } from 'react'
import type { CompanionPanelData } from '@/types/schemas/companion-panel-data'

const registry = new Map<string, Record<string, unknown>>()

export function registerPanelState(panelId: string, state: Record<string, unknown>): void {
  registry.set(panelId, state)
}

export function unregisterPanelState(panelId: string): void {
  registry.delete(panelId)
}

export function getPanelLiveState(panelId: string): Record<string, unknown> | null {
  return registry.get(panelId) ?? null
}

export function getAllPanelLiveStates(): Map<string, Record<string, unknown>> {
  return registry
}

/**
 * Register a panel's live state for the AI Companion. Re-registers whenever the
 * state's CONTENT changes (keyed on a serialized snapshot) and unregisters on
 * unmount. Adopt this in every panel type so `@`-mentioning it / reading it
 * gives the AI the panel's actual contents. Keep `state` compact and meaningful
 * (what a human would say is "in" the panel) — it goes into the prompt.
 */
export function useRegisterPanelState(panelId: string, state: Record<string, unknown>): void {
  const serialized = JSON.stringify(state)
  useEffect(() => {
    registerPanelState(panelId, state)
    return () => unregisterPanelState(panelId)
    // `state` is read via its serialized content key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelId, serialized])
}

/**
 * Compact a panel's live state into a prompt-friendly snapshot. Skips internal
 * `_`-prefixed keys (kept in state for tooling but noise for the model) and
 * null values; stringifies objects. Shared by `read_panel_state` and the
 * `@`-mention preamble so both describe a panel identically.
 */
export function formatPanelStateForPrompt(state: Record<string, unknown>): string {
  const lines: string[] = []
  for (const [key, val] of Object.entries(state)) {
    if (key.startsWith('_') || val == null) continue
    lines.push(`  ${key}: ${typeof val === 'object' ? JSON.stringify(val) : String(val)}`)
  }
  return lines.join('\n')
}

// ─── Structured panel data for the analyst ────────────────────────────────
// Live state above is prose for prompts. Panels that SHOW data (grids) also
// register a provider returning their rows as a table; @-mentioning the
// panel sends that table with the question as first-class evidence (see
// features/companion/turn-panels.ts and the server's panel-evidence.ts).

const dataProviders = new Map<string, () => CompanionPanelData | null>()

export function registerPanelDataProvider(
  panelId: string,
  provider: () => CompanionPanelData | null,
): void {
  dataProviders.set(panelId, provider)
}

export function unregisterPanelDataProvider(panelId: string): void {
  dataProviders.delete(panelId)
}

/** The panel's table right now, or null when it registers none (or is empty). */
export function getPanelData(panelId: string): CompanionPanelData | null {
  try {
    return dataProviders.get(panelId)?.() ?? null
  } catch {
    return null
  }
}
